//// Application Packages
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { object, string } from 'yup'
import { useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import { format } from 'date-fns'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionPage } from '../../functions/page-functions'

//// Application Slices
import { updateSettings, selectSettings } from '../../reducers/settingSlice'
import {
    createAuthorizations,
    selectAuthorizations,
} from '../../reducers/authorizationSlice'
import { createCache } from '../../reducers/cacheSlice'
import {
    createAccountData,
    createAccountAuxiliary,
    updateAccountData,
    updateAccountValidity,
    selectAccount,
} from '../../reducers/accountSlice'
import {
    createFormData,
    createFormAuxiliary,
    createSubFormData,
    createSubFormValidity,
    updateFormSystem,
} from '../../reducers/formSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, TextInput, Button, PasswordInput } from '@carbon/react'

//// Carbon Icons
import { Help, UserAvatar } from '@carbon/icons-react'

//// Static Constants

//////// Component
const LoginPage = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const auth = useSelector(selectAuthorizations)
    const accountData = useSelector(selectAccount)

    //// Slice Data
    const digital_name = settingsData?.['page']?.['digital_name']
    const written_name = settingsData?.['page']?.['written_name']
    const submit_route = settingsData?.['page']?.['submit_route']
    const fields = settingsData?.['page']?.['fields']
    const tokens = auth?.['tokens']

    //////// Search Params

    //////// Methods

    // Orchestrate Read Session Log (Integration)
    const orchestrateReadSessionLog = async (reguserid) => {
        try {
            const base_url = `${process.env.REACT_APP_BASE_URL}`
            const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_SYSTEM_SESSION_QUERY}`
            const url = base_url + api_endpoint
            const { data } = await axios.post(url, {
                prefix: 'reguserid',
                value: reguserid,
            })
            if (data) {
                return await data
            } else {
                console.log('Read Session Log: Response Is Undefined')
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Read Session Log Error:', error)
            return null
        }
    }

    // Orchestrate Login Data (Integration)
    const orchestrateLogin = async (accountData) => {
        try {
            if (accountData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_ACCOUNT_LOGIN}`
                const url = base_url + api_endpoint
                const { UserID, Password } = accountData

                // Create Request
                if (UserID && Password) {
                    const { data } = await axios.post(url, {
                        userid: UserID,
                        password: Password,
                    })
                    if (data) {
                        return data
                    } else {
                        console.log(`Account Login: Response Is Undefined`)
                        return null
                    }
                }
            } else {
                console.log(`Account Login: Request Is Undefined`)
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Login Error:', error)
            return null
        }
    }

    // Re-Validate Data (Utility)
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateAccountValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
                return true
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    // Validate Data (Check)
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                UserID: string()
                    .min(0)
                    .max(50)
                    .required(
                        'This field is required to be entered or selected.'
                    ),
                Password: string()
                    .min(0)
                    .max(50)
                    .required(
                        'This field is required to be entered or selected.'
                    ),
            })
            schema.valid = {
                UserID: false,
                UserIDErrMsg: '',
                Password: false,
                PasswordErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle Data + Redact Data (Method)
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Clear Data (Soft Reset)
    const handleClearData = () => {
        //// Perform Data Clearing

        dispatch(
            updateAccountData({
                digital_name: digital_name,
                values: {
                    UserID: '',
                    Password: '',
                },
            })
        )

        //// End Data Clearing
    }

    // Handle Submission (Method)
    const handleSubmit = async () => {
        try {
            console.info('Account Login: Submitted')

            // Extract Login Data From Redux
            const submit = await extractSubmissionPage(
                digital_name,
                accountData
            )

            // Validate Login Submission Data
            const valid = await validation(submit)

            // Check If Login Data Is Valid
            if (valid) {
                console.info('Account Login: Passed Valid')

                setLoading(true)

                // Send Orchestration Request
                const orchestrate = await orchestrateLogin(submit)

                console.info('Account Login: Passed Orchestrate')

                // Check For User To Restore A Session
                if (orchestrate?.reguserid) {
                    // Call Read Session Database API
                    const restore = await orchestrateReadSessionLog(
                        orchestrate?.reguserid
                    )

                    // Check For Session To Restore
                    if (
                        tokens?.form ===
                            restore?.['session_data']?.['authorizations']?.[
                                'tokens'
                            ]?.['form'] &&
                        restore?.['session_exists'] &&
                        restore?.['session_data']
                    ) {
                        console.info('Creating Session From Database')

                        const now = format(
                            new Date(),
                            "yyyy-MM-dd'T'HH:mm:ssXX"
                        ).toString()

                        // Dispatch Session Data To Page Data
                        if (restore?.['session_data']?.['pages']) {
                            // Restore Pages Defaults
                            const pages = restore?.session_data?.pages
                            if (pages) {
                                const pageTitles = Object?.keys(pages)
                                if (pageTitles?.length > 0) {
                                    pageTitles.forEach((el) => {
                                        const title = el
                                        const page = page?.[el]
                                        if (page?.['auxiliary']) {
                                            const auxiliary = page?.auxiliary
                                            dispatch(
                                                createAccountAuxiliary({
                                                    digital_name: title,
                                                    auxiliary: auxiliary,
                                                })
                                            )
                                        }
                                    })
                                }
                            }
                        }

                        // Dispatch Session Data To Form Data
                        if (restore?.['session_data']?.['forms']) {
                            const forms = restore?.session_data?.forms
                            if (forms) {
                                const formTitles = Object?.keys(forms)
                                if (formTitles?.length > 0) {
                                    formTitles.forEach((el) => {
                                        const title = el
                                        const form = forms?.[el]
                                        if (form?.['values']) {
                                            const values = form?.values
                                            dispatch(
                                                createFormData({
                                                    digital_name: title,
                                                    values: values,
                                                })
                                            )
                                        }
                                        if (form?.['auxiliary']) {
                                            const auxiliary = form?.auxiliary
                                            dispatch(
                                                createFormAuxiliary({
                                                    digital_name: title,
                                                    auxiliary: auxiliary,
                                                })
                                            )
                                        }
                                        if (form?.['system']) {
                                            const system = form?.system
                                            dispatch(
                                                updateFormSystem({
                                                    digital_name: title,
                                                    system: system,
                                                })
                                            )
                                        }
                                        if (
                                            !form?.['values'] &&
                                            !form?.['auxiliary']
                                        ) {
                                            const subFormTitles =
                                                Object?.keys(form)
                                            if (subFormTitles?.length > 0) {
                                                subFormTitles.forEach((el) => {
                                                    const subTitle = el
                                                    const subForm = form?.[el]
                                                    if (subForm?.['values']) {
                                                        const subFormValues =
                                                            subForm?.values
                                                        dispatch(
                                                            createSubFormData({
                                                                digital_name:
                                                                    title,
                                                                digital_sub_name:
                                                                    subTitle,
                                                                values: subFormValues,
                                                            })
                                                        )
                                                    }
                                                    if (
                                                        subForm?.['auxiliary']
                                                    ) {
                                                        const subFormAuxiliary =
                                                            subForm?.auxiliary
                                                        dispatch(
                                                            createSubFormValidity(
                                                                {
                                                                    digital_name:
                                                                        title,
                                                                    digital_sub_name:
                                                                        subTitle,
                                                                    auxiliary:
                                                                        subFormAuxiliary,
                                                                }
                                                            )
                                                        )
                                                    }
                                                })
                                            }
                                        }
                                    })
                                }
                            }
                        }

                        // Dispatch Session Data To Cache
                        dispatch(
                            createCache({
                                settings: restore?.session_data?.settings,
                                authorizations:
                                    restore?.session_data?.authorizations,
                                account: restore?.session_data?.account,
                                forms: restore?.session_data?.forms,
                            })
                        )

                        // Dispatch Session Data To Settings
                        dispatch(
                            updateSettings({
                                digital_name: 'session',
                                settings: {
                                    active: true,
                                    created: now,
                                    saved: now,
                                },
                            })
                        )
                    } else {
                        console.info('Session Restoration: Failed')

                        console.log(
                            'Session Restoration Failed: Form/Session Mismatch'
                        )
                    }
                }

                // Check If Orchestration Processed Submission
                if (
                    orchestrate?.reguserid &&
                    orchestrate?.login_status === 'Y' &&
                    orchestrate?.login_success &&
                    orchestrate?.cookie
                ) {
                    console.info('Account Login: Succcess')

                    setLoading(false)
                    dispatch(
                        createAccountData({
                            digital_name: digital_name,
                            values: orchestrate,
                        })
                    )
                    dispatch(
                        createAuthorizations({
                            cookie: orchestrate.cookie,
                        })
                    )
                    setNotification({
                        type: 'inline',
                        visible: false,
                        kind: '',
                        title: '',
                        subtitle: '',
                    })

                    // Check If Link Goes To Form
                    if (tokens?.form) {
                        // Navigate To Next Form
                        navigate('/' + tokens?.form)

                        // Clear Existing Data
                        handleClearData()
                    } else {
                        // Navigate To Next Form
                        navigate(submit_route ? submit_route : '/')

                        // Clear Existing Data
                        handleClearData()
                    }
                } else if (
                    orchestrate?.reguserid &&
                    orchestrate?.login_status === 'T' &&
                    !orchestrate?.login_success
                ) {
                    console.info('Account Login: Succcess (Temporary)')

                    setLoading(false)
                    dispatch(
                        createAccountData({
                            digital_name: digital_name,
                            values: orchestrate,
                        })
                    )
                    setNotification({
                        type: 'inline',
                        visible: false,
                        kind: '',
                        title: '',
                        subtitle: '',
                    })

                    // Clear Existing Data
                    dispatch(
                        updateAccountData({
                            digital_name: digital_name,
                            values: {
                                Password: '',
                            },
                        })
                    )

                    // Navigate To Next Form
                    navigate('/account/temporary-password')
                } else if (
                    !orchestrate?.acc_login &&
                    orchestrate?.login_status &&
                    !orchestrate?.login_success
                ) {
                    console.info('Account Login: Failed')

                    setLoading(false)
                    dispatch(
                        updateAccountValidity({
                            digital_name: digital_name,
                            errors: {
                                UserID: false,
                                UserIDErrMsg: 'Account Credentials Invalid',
                                Password: false,
                                PasswordErrMsg: 'Account Credentials Invalid',
                            },
                        })
                    )
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Login Failed:',
                        subtitle: 'Account Credentials Invalid',
                    })

                    console.log(
                        'Account Login Failed: Account Credentials Invalid'
                    )
                } else {
                    console.info('Account Login: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Login Failed:',
                        subtitle: 'Account Credentials Are Invalid',
                    })

                    console.log(
                        'Account Login Failed: Account Credentials Are Invalid'
                    )
                }
            } else {
                console.info('Account Login: Failed')

                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Account Login Failed:',
                    subtitle:
                        'Please Fix All Validation Errors Before Trying To Resubmit',
                })

                console.log(
                    'Account Login Failed: Please Fix All Validation Errors Before Trying To Resubmit'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Key Down Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <div className="form">
                    <h2 className="form-title">
                        {written_name ? written_name : ``}
                    </h2>
                    <LoadingComponent />
                </div>
            </>
        )
    } else {
        return (
            <>
                <div
                    className="form"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-testid="account-login-page"
                >
                    <h2
                        className="form-title"
                        aria-level="1"
                        title="page-heading"
                    >
                        {written_name ? written_name : ``}
                    </h2>

                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Credentials" />

                        <p className="introduction-paragraph">
                            <strong>
                                First time here? Click{' '}
                                <Link to={'/account/registration'}>
                                    {'"'}Create New Account{'"'}
                                </Link>{' '}
                                to start your licensing journey!
                            </strong>
                        </p>

                        <TextInput
                            className="required"
                            labelText="User ID:"
                            id="UserID"
                            name="UserID"
                            autocomplete="username"
                            placeholder="User ID"
                            value={accountData?.[digital_name]?.values?.UserID}
                            maxLength="50"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.UserID
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.UserIDErrMsg
                            }
                        />

                        <span className="required">
                            <PasswordInput
                                className="required"
                                labelText="Password:"
                                id="Password"
                                name="Password"
                                autocomplete="current-password"
                                placeholder="Password"
                                value={
                                    accountData?.[digital_name]?.values
                                        ?.Password
                                }
                                maxLength="50"
                                helperText=""
                                hidePasswordLabel="Hide Password"
                                showPasswordLabel="Show Password"
                                onChange={handleData}
                                invalid={
                                    accountData?.[digital_name]?.errors
                                        ?.Password
                                }
                                invalidText={
                                    accountData?.[digital_name]?.errors
                                        ?.PasswordErrMsg
                                }
                            />
                        </span>

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Login"
                                    tabIndex="0"
                                    role="button"
                                    aria-pressed="false"
                                    aria-label="Login"
                                />
                            </div>
                        </div>
                    </Form>
                </div>

                <NotificationComponent notification={notification} />

                <DividerComponent text="Alternative Account Options" />

                <div className="button-grouping">
                    <div className="full-width-button-with-icon-group">
                        <div className="full-width-button-with-icon-group-item">
                            <Link to={`/account/registration`}>
                                <Button
                                    className="fldoh-btn"
                                    type="button"
                                    kind="secondary"
                                    size="lg"
                                    disabled={false}
                                    role="button"
                                >
                                    Create New Account
                                    <UserAvatar />
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <div className="full-width-button-with-icon-group">
                        <div className="full-width-button-with-icon-group-item">
                            <Link to={`/account/forgot-user-id`}>
                                <Button
                                    className="fldoh-btn"
                                    type="button"
                                    kind="tertiary"
                                    size="lg"
                                    disabled={false}
                                    role="button"
                                >
                                    Forgot Account User ID
                                    <Help />
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <div className="full-width-button-with-icon-group">
                        <div className="full-width-button-with-icon-group-item">
                            <Link to={`/account/reset-password`}>
                                <Button
                                    className="fldoh-btn"
                                    type="button"
                                    kind="tertiary"
                                    size="lg"
                                    disabled={false}
                                >
                                    Forgot Account Password
                                    <Help />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                <span
                    className="visually-hidden"
                    data-testid="account-login-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    }
}

export default LoginPage
